/* eslint-disable @typescript-eslint/no-explicit-any */
import { ENDPOINTS } from '@/util'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useGetFilterSelections } from '@/catalog-apps/hooks/useGetFilterSelections'
import { isStatementEditorV2UISchema, StatementEditorV2Response } from './types'
import { addPositionToContentBlocks } from './utils'
import { useAppContext } from '@/catalog-apps/hooks/useAppContext'
import { useGetPageFilters } from '../../AppPage2/Filter/useGetPageFilters'
import { useIsPageBuilder } from '@/catalog-apps/hooks/useIsPageBuilder'
import { TComponentType } from '../../AppPage2/types'

// Set to true to use mock data, false to use actual data
const USE_MOCK_DATA = false

const enhanceStatementEditorV2Response = (response: StatementEditorV2Response): StatementEditorV2Response => {
  if (!isStatementEditorV2UISchema(response.uiSchema)) {
    return response
  }
  const enhanced = { ...response, dataSchema: { ...response.dataSchema } }
  const { indexData, contentData } = enhanced.dataSchema
  enhanced.dataSchema.contentData = addPositionToContentBlocks(contentData, indexData)
  return enhanced
}

interface GetStatementEditorV2Params {
  appKey?: string
  contentId?: string
  projectId?: number
  filterSelections: Record<string, any>[]
  isPageBuilder?: boolean
}

const getStatementEditorV2 = async ({
  appKey,
  contentId,
  projectId,
  filterSelections,
  isPageBuilder,
}: GetStatementEditorV2Params) => {
  if (!appKey || !contentId || !projectId) {
    throw new Error('Missing required parameters')
  }
  if (isPageBuilder) {
    const data = (await import('@/catalog-apps/mock-data/statement-editor-v2/statement-editor-v2-response.json'))
      .json as StatementEditorV2Response
    return Promise.resolve(enhanceStatementEditorV2Response(data))
  }
  if (USE_MOCK_DATA) {
    const data = (await import('@/catalog-apps/mock-data/statement-editor-v2/statement-editor-v2-response-v2.json'))
      .json as StatementEditorV2Response
    return Promise.resolve(enhanceStatementEditorV2Response(data))
  }

  const token = JSON.parse(sessionStorage.getItem('token') || '')
  if (!token) {
    throw new Error('No token found')
  }

  const url = `${ENDPOINTS.CC_DOTNET}/content`
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      app_id: appKey,
      content_id: contentId,
      project_id: projectId,
      filters: filterSelections,
    }),
  })

  const data = (await response.json()) as StatementEditorV2Response
  if (response.ok) {
    return enhanceStatementEditorV2Response(data)
  }
  throw new Error('Failed to fetch statementEditorV2 data')
}

export function useGetStatementEditorV2(contentId: string, tileFilterSelections: Record<string, any>[]) {
  const isPageBuilder = useIsPageBuilder()
  const {
    data: { defaultSelections },
  } = useGetPageFilters()
  const { filterSelections } = useGetFilterSelections()
  const { appKey, projectId } = useAppContext()
  const filterSelectionsArray =
    filterSelections.length > 0
      ? [...filterSelections, ...tileFilterSelections]
      : [...defaultSelections, ...tileFilterSelections]

  const componentType: TComponentType = 'statement-editor-v2'
  return useSuspenseQuery({
    queryKey: [contentId, componentType, appKey, projectId, filterSelectionsArray],
    queryFn: () =>
      getStatementEditorV2({
        appKey,
        contentId,
        projectId,
        filterSelections: filterSelectionsArray,
        isPageBuilder,
      }),
  })
}
